import prettyBytes from 'pretty-bytes';
import {
    isDimensions,
    isFile,
    isFilled,
    isIntegerLike,
    isMax,
    isMimeType,
} from '@/utils/form/validationRules';
import {
    descriptionCharactersLimit,
    audioFileAllowedMimeTypes,
    audioFileMaxSize,
} from '@/utils/config/episodeOptions';
import htmlToText from '@/utils/htmlToText';

/**
 * Performs base validation of Episode Title.
 *
 * @param   {string}       title  Episode Title.
 * @returns {string|null}  Validation result.
 */
export const validateTitle = (title) => {
    if (! isFilled(title)) {
        return 'Please provide Episode Title.';
    }

    if (! isMax(title, 250)) {
        return 'Please shorten Episode Title (max 250 characters).';
    }

    return null;
};

/**
 * Performs base validation of Episode Description.
 *
 * @param   {string}       description  Episode Description.
 * @returns {string|null}  Validation result.
 */
export const validateDescription = (description) => {
    if (! isFilled(description)) {
        return null;
    }

    if (! isMax(htmlToText(description), descriptionCharactersLimit)) {
        return `Please shorten Episode Description (max ${descriptionCharactersLimit} characters).`;
    }

    return null;
};

/**
 * Performs base validation of Episode Number.
 *
 * @param   {string}      episodeNo  Episode Number.
 * @returns {string|null}            Validation result.
 */
export const validateEpisodeNo = (episodeNo) => {
    if (! isFilled(episodeNo)) {
        return 'Please provide Episode Number';
    }

    if (! isIntegerLike(episodeNo)) {
        return 'Please provide valid Episode Number';
    }

    return null;
};

/**
 * Performs base validation of Episode Season Number.
 *
 * @param   {string}      podcastType  Podcast type.
 * @param   {string}      seasonNo     Episode Season Number.
 * @returns {string|null}              Validation result.
 */
export const validateSeasonNo = (podcastType, seasonNo) => {
    if ('serial' === podcastType) {
        if (! isFilled(seasonNo)) {
            return 'Please provide Season Number';
        }

        if (! isIntegerLike(seasonNo)) {
            return 'Please provide valid Season Number';
        }
    }

    return null;
};

/**
 * Performs base validation of Episode Artwork.
 *
 * @param   {File|null}    artwork                  Episode Artwork.
 * @param   {object}       config                   Validation configuration.
 * @param   {string[]}     config.allowedMimeTypes  Artwork allowed MIME types.
 * @returns {string|null}                           Validation result.
 */
export const validateArtwork = async (artwork, { allowedMimeTypes }) => {
    if (! isFile(artwork)) {
        return null; // do not validate if is not file
    }

    if (! isMimeType(artwork, allowedMimeTypes)) {
        return 'Please provide Episode Artwork in valid format.';
    }

    if (! await isDimensions(
        artwork,
        { minHeight: 1400, minWidth: 1400, maxHeight: 3000, maxWidth: 3000 }
    )) {
        return 'Please provide Episode Artwork with correct size.';
    }

    if (! await isDimensions(artwork, { ratio: 1 / 1 })) {
        return 'Please provide Episode Artwork which is square.';
    }

    return null;
};

/**
 * Performs base validation of Episode Audio File.
 *
 * @param   {File|null}    file  Episode Audio File.
 * @returns {string|null}        Validation result.
 */
export const validateAudioFile = (file) => {
    if (! isFile(file)) {
        return 'Please provide valid Episode Audio file.';
    }

    if (! isMimeType(file, audioFileAllowedMimeTypes)) {
        return 'Please provide Episode Audio in valid audio format.';
    }

    if (! isMax(file, audioFileMaxSize)) {
        return `Please provide Episode Audio with smaller size (${prettyBytes(audioFileMaxSize)} limit).`;
    }

    return null;
};
